import { Component, Fragment, Prop, h } from '@stencil/core';

import { actions } from '../../actions/actions.interactive-banner';
import { ContainerSize } from '../../interfaces';
import { SelectOption } from '../../interfaces/interface.banner';
import { responsiveContainer } from '../../utils/utils.container';
import {
  BannerInstallationMode,
  BannerStep,
  BannerStepStorage,
  ComponentDirection,
  bannerMinSizes,
  bannerSizeType,
  getBannerSize,
  getLPError,
  getMileageError,
  getStyleError,
  getVinError,
  getYMMError,
  translateTabValue,
} from '../../utils/utils.interactive-banner';

@Component({
  tag: 'interactive-banner',
  styleUrl: 'interactive-banner.css',
  shadow: true,
})
export class InteractiveBanner {
  @Prop() installationMode: BannerInstallationMode;
  @Prop() tabHeaders: [] = [];
  @Prop() defaultSelectedTab: string = 'vin';
  @Prop() states: SelectOption[] = [];
  @Prop() yearId: number;
  @Prop() make: string;
  @Prop() model: string;
  @Prop() trim: string;
  @Prop() currentSelectedTab: string;
  @Prop() ymmSuggestions = [];
  @Prop() vin: string;
  @Prop() mileage: number;
  @Prop() isFetching: boolean;
  @Prop() activeStep: BannerStep;
  @Prop() trimId: number;
  @Prop() vehiclePossibilities: SelectOption[];
  @Prop() furyUrl: string;
  @Prop() inputVin: string;
  @Prop() inputYmm: string;
  @Prop() vehicleId: string;
  @Prop() errors: Set<string>;
  @Prop() optimizelyBannerIsActive: boolean;
  @Prop() isServicesHealthy: boolean;
  @Prop() isOpenModal: boolean;
  @Prop() licensePlateNumber: string;
  @Prop() licensePlateState: string;
  @Prop() zIndex: number = 2000;
  @Prop() amount: number;
  @Prop() markupAmounts: object = {};
  @Prop() totalOfferAmounts: object = {};
  @Prop() expirationDate: string = '';
  @Prop() isActiveListing: boolean = false;
  @Prop() activeDealerListingUrl: string = '';
  @Prop() sdContainer: string = '';
  @Prop() sdOemDealerCode: string = '';

  hasMultipleTrims() {
    return this.vehiclePossibilities.length > 1;
  }

  render() {
    let textLink = '';
    const isShowText = this.activeStep !== BannerStep.FormSelector && this.activeStep !== BannerStep.UnSubscribed;
    const vinError = getVinError(this.errors);
    const lpError = getLPError(this.errors);
    const ymmError = getYMMError(this.errors);
    let styleError = undefined;
    let mileageError = undefined;

    const bannerFormSelectorClass = (size: boolean) => {
      return size ? 'w-full pt-1 pb-1' : 'w-full';
    };
    const bannerHeightClass = (size: boolean) => {
      return size ? 'h-[133px]' : 'h-[310px]';
    };
    const bannerHeadingClass = (showDesktop: boolean) => {
      return {
        'flex flex-col justify-center': showDesktop,
      };
    };

    const bannerLogoClass = (size: bannerSizeType) => {
      return {
        'h-full': size === 'large' || size === 'small' || size === 'xsmall',
        'absolute right-7': size === 'medium' && this.activeStep !== BannerStep.MSM && this.activeStep !== BannerStep.ThankYou,
      };
    };

    if (isShowText) {
      textLink = this.activeStep === BannerStep.ThankYou ? 'Start a new offer' : 'Change vehicle';
    }

    if (this.activeStep === BannerStep.MSM) {
      styleError = getStyleError(this.errors);
      mileageError = getMileageError(this.errors);
      actions.saveCurrentStepToSessionStorage(BannerStepStorage.BannerCurrenStep);
    }

    const shouldRenderMaintenanceBanner = !this.optimizelyBannerIsActive || !this.isServicesHealthy;

    const renderScreen = (size: ContainerSize) => {
      if (this.isFetching) {
        return <banner-loading class={bannerHeightClass(size.breakpoints.lg)} showDesktop={size.breakpoints.lg} />;
      } else if (this.activeStep === BannerStep.NotQualify) {
        return (
          <banner-disqualified
            class={bannerHeightClass(size.breakpoints.lg)}
            showDesktop={size.breakpoints.lg}
            showCondensed={!size.breakpoints.md}
            headingSize={getBannerSize(size, ComponentDirection.Heading)}
          />
        );
      } else if (this.activeStep === BannerStep.Continue) {
        return (
          <banner-continue
            class="w-full"
            yearId={this.yearId}
            make={this.make}
            model={this.model}
            trim={this.trim}
            showDesktop={size.breakpoints.lg}
            continueSize={getBannerSize(size)}
          />
        );
      } else if (this.activeStep === BannerStep.Eligible) {
        return (
          <banner-eligible
            amount={this.amount}
            markupAmounts={this.markupAmounts}
            totalOfferAmounts={this.totalOfferAmounts}
            yearId={this.yearId}
            make={this.make}
            model={this.model}
            trim={this.trim}
            expirationDate={this.expirationDate}
            bannerEligibleSize={getBannerSize(size)}
          />
        );
      } else if (this.activeStep === BannerStep.Review || this.activeStep === BannerStep.Inspection) {
        return (
          <banner-review-inspection
            yearId={this.yearId}
            make={this.make}
            model={this.model}
            trim={this.trim}
            bannerStatus={this.activeStep}
            bannerReviewInspectionSize={getBannerSize(size)}
          />
        );
      } else if (shouldRenderMaintenanceBanner) {
        return <banner-maintenance-page class={bannerHeightClass(size.breakpoints.lg)} showDesktop={size.breakpoints.lg} />;
      }

      return (
        <banner-container showDesktop={size.breakpoints.lg}>
          {this.activeStep !== BannerStep.UnSubscribed && (
            <banner-heading
              class={bannerHeadingClass(size.breakpoints.lg)}
              hiddenOnMobile={this.activeStep === BannerStep.ThankYou || this.activeStep === BannerStep.MSM}
              showDesktop={size.breakpoints.lg}
              headingSize={getBannerSize(size, ComponentDirection.Heading)}
            />
          )}

          {this.activeStep === BannerStep.FormSelector && (
            <dynamic-form-selector
              tabs={this.tabHeaders}
              selectedTab={translateTabValue(this.currentSelectedTab)}
              defaultTab={translateTabValue(this.defaultSelectedTab)}
              changeTab={actions.setCurrentSelectedTab}
              componentPrefix="banner-"
              class={bannerFormSelectorClass(size.breakpoints.lg)}
              showDesktop={size.breakpoints.lg}
              tabAttributes={{
                vin: {
                  vin: this.vin,
                  hasError: !!vinError,
                  errorMessage: vinError,
                  showDesktop: size.breakpoints.lg,
                  bannerVinSize: getBannerSize(size),
                },
                licensePlate: {
                  states: this.states,
                  licensePlateNumber: this.licensePlateNumber,
                  licensePlateState: this.licensePlateState,
                  hasError: !!lpError,
                  errorMessage: lpError,
                  showDesktop: size.breakpoints.lg,
                  bannerSize: getBannerSize(size),
                },
                ymm: {
                  ymmSuggestions: this.ymmSuggestions,
                  vehicleId: this.vehicleId,
                  value: this.inputYmm,
                  hasError: !!ymmError,
                  showDesktop: size.breakpoints.lg,
                  bannerSize: getBannerSize(size),
                  errorMessage: ymmError,
                },
              }}
            />
          )}

          {this.activeStep === BannerStep.MSM && (
            <banner-msm
              vehiclePossibilities={this.vehiclePossibilities}
              yearId={this.yearId}
              make={this.make}
              model={this.model}
              vin={this.vin}
              trimId={this.trimId}
              trim={this.trim}
              hasMultipleTrims={this.hasMultipleTrims()}
              hasStyleError={!!styleError}
              hasMileageError={!!mileageError}
              errorMessage={styleError || mileageError}
              showDesktop={size.breakpoints.lg}
              isActiveListing={this.isActiveListing}
              activeDealerListingUrl={this.activeDealerListingUrl}
              class="w-full"
            />
          )}

          {this.activeStep === BannerStep.ThankYou && (
            <banner-thank-you
              yearId={this.yearId}
              make={this.make}
              model={this.model}
              vin={this.vin}
              mileage={this.mileage}
              trim={this.trim}
              showDesktop={size.breakpoints.lg}
              class="w-full pb-2"
            />
          )}

          {this.activeStep === BannerStep.UnSubscribed && <banner-unsubscribed showDesktop={size.breakpoints.lg} class="w-full" />}

          <banner-logo
            class={bannerLogoClass(getBannerSize(size))}
            textLink={textLink}
            showDesktop={size.breakpoints.lg}
            logoWithMPTop={!size.breakpoints.xs || (size.breakpoints.lg && this.activeStep === BannerStep.MSM)}
          />
        </banner-container>
      );
    };

    return responsiveContainer(
      (size) => (
        <Fragment>
          {!!this.sdContainer && <ico-analytics-shift-digital container={this.sdContainer} oemDealerCode={this.sdOemDealerCode} />}
          {renderScreen(size)}
          {this.furyUrl && <banner-next url={this.furyUrl} isOpen={this.isOpenModal} showDesktop={size.breakpoints.lg} zIndex={this.zIndex} />}
        </Fragment>
      ),
      bannerMinSizes
    );
  }
}
